
import { useState } from "react";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserTimeEntry } from "@/hooks/useUserTimeEntries";
import { generateSalaryPDF } from "@/utils/pdfGenerator";
import { getEmployeePayrollOverride, calculateFixedNetPayroll } from "@/utils/payrollOverrides";
import { generateSwissPayslip, downloadPayslip, type SwissPayslipData, type TimeSubmissionData } from "@/utils/swissPayslipGenerator";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { COMPANY_INFO } from "@/config/companyInfo";
import { round2, secondsToHours, calculateMonthlySoll, deriveContractParams } from "@/utils/workTimeUtils";
import { getEmployeeOverride } from "@/config/employeeOverrides";

interface UserTimeEntriesHeaderProps {
  userEmail: string;
  timeEntries?: UserTimeEntry[];
}

/** Build Monatsabschluss data for the current month. */
async function buildTimeSubmissionForCurrentMonth(
  employeeId: string,
  employee: Parameters<typeof deriveContractParams>[0] & { email?: string | null },
  currentDate: Date
): Promise<TimeSubmissionData> {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const mm = month.toString().padStart(2, "0");
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMM = nextMonth.toString().padStart(2, "0");

  // Fetch time entries for this month
  const { data: timeData } = await supabase
    .from("time_entries")
    .select("duration")
    .eq("user_id", employeeId)
    .is("deleted_at", null)
    .gte("start_time", `${year}-${mm}-01`)
    .lt("start_time", `${nextYear}-${nextMM}-01`);

  const actualHours = round2(
    (timeData || []).reduce((s, e) => s + secondsToHours(e.duration || 0), 0)
  );

  // Fetch vacation hours for this month
  const { data: vacData } = await supabase
    .from("vacation_transactions")
    .select("days_amount")
    .eq("employee_id", employeeId)
    .eq("transaction_type", "taken")
    .gte("transaction_date", `${year}-${mm}-01`)
    .lt("transaction_date", `${nextYear}-${nextMM}-01`);

  const vacationHours = round2(
    (vacData || []).reduce((s, t) => s + (t.days_amount || 0), 0)
  );

  const overrides = employee.email ? getEmployeeOverride(employee.email) : undefined;
  const { monthlyTarget, canton, contractStart } = deriveContractParams(employee, overrides);

  const sollResult = calculateMonthlySoll(monthlyTarget, year, month, canton, {
    includeClosures: true,
    contractStart:
      contractStart && contractStart > new Date(year, month - 1, 1)
        ? contractStart
        : undefined,
    vacationHours,
  });

  return {
    actualHours,
    expectedHours: sollResult.soll,
    difference: round2(actualHours - sollResult.soll),
    workingDays: sollResult.workingDays,
    vacationHours,
    holidays: sollResult.holidays.map((h) => h.name),
  };
}

export function UserTimeEntriesHeader({
  userEmail,
  timeEntries,
}: UserTimeEntriesHeaderProps) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const generateSwissPayslipForEmployee = async (employeeId: string, employeeName: string) => {
    const { data: employee } = await supabase
      .from("employees")
      .select("*")
      .eq("id", employeeId)
      .single();

    if (!employee) throw new Error("Employee not found");

    const payrollResult = await calculateFixedNetPayroll(employeeId, employee.birth_date);
    if (!payrollResult) throw new Error("Could not calculate payroll");

    const currentDate = new Date();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    // Build mandatory Monatsabschluss
    const timeSubmission = await buildTimeSubmissionForCurrentMonth(
      employeeId,
      employee,
      currentDate
    );

    const payslipData: SwissPayslipData = {
      employee: {
        id: employee.id,
        name: `${employee.firstname} ${employee.lastname}`,
        address: employee.address,
        ahv_number: employee.ahv_number,
      },
      company: COMPANY_INFO,
      payroll: {
        employee_id: employeeId,
        gross_salary: payrollResult.gross_salary,
        net_salary: payrollResult.net_salary,
        total_deductions: payrollResult.total_deductions,
        deductions: payrollResult.deductions,
        hours_worked: 0,
        base_salary: payrollResult.gross_salary,
        vacation_days_taken: 0,
        vacation_pay_included: false,
      },
      period: {
        start_date: firstDayOfMonth.toISOString(),
        end_date: lastDayOfMonth.toISOString(),
        payment_date: lastDayOfMonth.toISOString(),
      },
      timeSubmission,
      minimal: payrollResult.payslip_minimal,
    };

    const payslipBlob = await generateSwissPayslip(payslipData);
    downloadPayslip(payslipBlob, employeeName, format(currentDate, "yyyy-MM"));

    toast({ title: "Erfolg", description: "Gehaltszettel erfolgreich erstellt" });
  };

  const handleExportPDF = async () => {
    if (!timeEntries?.length || isExporting) return;

    setIsExporting(true);
    try {
      const employeeId = timeEntries[0].user_id;
      const override = await getEmployeePayrollOverride(employeeId);

      if (override) {
        await generateSwissPayslipForEmployee(employeeId, userEmail);
      } else {
        generateSalaryPDF(timeEntries);
      }
    } catch (error) {
      console.error("Error generating payslip:", error);
      toast({
        title: "Fehler",
        description: "Gehaltszettel konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DialogHeader>
      <div className="flex justify-between items-center">
        <DialogTitle>Time Entries for {userEmail}</DialogTitle>
        {timeEntries?.length ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={isExporting}
            className="ml-4"
          >
            <FileDown className="mr-2 h-4 w-4" />
            {isExporting ? "Exportiere..." : "PDF Export"}
          </Button>
        ) : null}
      </div>
    </DialogHeader>
  );
}
